import { VStack } from '@chakra-ui/react';
import { useMemo, useState } from 'react';

import { useJournalAnalytics } from 'app/analytics/useJournalAnalytics';
import { SidebarBody, useSidebarContext, Box, Loader, For } from 'shared';
import Spacer from 'shared_DEPRECATED/components/Spacer';
import { Nullable } from 'shared_DEPRECATED/types';
import { USER_TYPES } from 'shared_DEPRECATED/types/User';

import {
  JournalNotesContainer,
  useJournalContext,
  JournalNote,
  useJournalEditNoteMutation,
  JournalForm,
  useJournalMetadataQuery,
  JournalFormHeader,
  JournalFormHeaderTitle,
  JournalAddNoteButton,
  getNotesItems,
  JournalItemType,
} from 'features/journal';
import { User } from 'features/user/User';
import { useUserMode } from 'features/user/hooks';

import { Show } from 'shared/components/Show';

import { JournalNoteItem } from './Body/Item';

export const JournalNotesBody = () => {
  const [editedNote, setEditedNote] = useState<Nullable<JournalNote>>(null);
  const { notes, scrollRef, isFetchingNextPage } = useJournalContext();
  const { user, participantId } = useSidebarContext() as {
    user: User;
    participantId: string;
  };
  const { mutateAsync: editNote } = useJournalEditNoteMutation({
    participantId,
  });
  const { sendNoteEditedAnalyticsEvent } = useJournalAnalytics();

  const userMode = useUserMode();

  const { data: { sprints } = { sprints: [] } } = useJournalMetadataQuery({
    participantId,
  });

  const groupedNotes = useMemo(() => {
    if (!notes) {
      return [];
    }

    return getNotesItems(notes, sprints);
  }, [notes, sprints]);

  const handleEditNote = async ({ text }: { text: string }) => {
    const noteId = editedNote?.noteId ?? '';
    setEditedNote(null);

    await editNote({ text, noteId });

    sendNoteEditedAnalyticsEvent();
  };

  return (
    <SidebarBody dataTestid="notes-sidebar-body">
      {userMode === USER_TYPES.USER && !editedNote && <JournalAddNoteButton />}
      <JournalNotesContainer>
        <Show when={Boolean(notes)}>
          {() => (
            <>
              <VStack p="0 0.5rem 0 1rem">
                <For each={groupedNotes}>
                  {({ type, data }: JournalItemType, index: number) => (
                    //@ts-ignore
                    <JournalNoteItem
                      key={index}
                      type={type}
                      data={data}
                      user={user}
                      participantId={participantId}
                      setEditedNote={setEditedNote}
                    />
                  )}
                </For>
              </VStack>
              {isFetchingNextPage ? (
                <Spacer size="md">
                  <Loader />
                </Spacer>
              ) : (
                <div ref={scrollRef}></div>
              )}
            </>
          )}
        </Show>
      </JournalNotesContainer>
      {editedNote && (
        <Box
          position="sticky"
          bottom="0"
          bg="var(--bgPrimary)"
          p="1rem"
          boxShadow="0 -2px 4px rgba(0, 0, 0, 0.1)"
        >
          <JournalForm
            text={editedNote.text}
            onSave={handleEditNote}
            placeholderText="Edit note text..."
          >
            <JournalFormHeader onCancel={() => setEditedNote(null)}>
              <JournalFormHeaderTitle>Edit note</JournalFormHeaderTitle>
            </JournalFormHeader>
          </JournalForm>
        </Box>
      )}
    </SidebarBody>
  );
};
